/**
 * Builds the purchase event exactly as the GTM server container expects it. crm-service forwards the
 * body untouched apart from injecting the caller's IP and user agent, so the shape lives here.
 *
 * `transaction_id` and `event_id` must stay identical to the `transaction_id` `purchaseTracking`
 * puts on the dataLayer event, or Meta counts two conversions instead of one.
 */
import { hashPii, hashPiiCompact } from '@/lib/utils/cryptoUtils'

import { type GetCheckoutSessionDetailsResponse, PriceProductType } from '@/clients/payment-service'

import { combineComplexTrialLineItems } from './complexTrialUtils'
import { excludePaymentApiShippingFeeLineItems } from './shippingFeeLineItems'

/** The container's schema. Snake_case throughout: it is sent as-is. */
export type SgtmPurchaseEvent = {
  event_name: 'Purchase'
  event_time: number
  event_id: string
  page_data: { section: string; page_name: string }
  purchase_data: {
    total_value: number
    currency: string
    transaction_id: string
    checkout_id: string
    product_name?: string
    product_type?: string
    trial_length?: number
    items: Array<{
      item_id: string
      item_name?: string
      price?: number
      quantity?: number
      item_brand: string
      item_category?: string
      item_category4?: string
      trial_length_days?: number
    }>
  }
  // ip_override and user_agent are added by crm-service from the request headers.
  user_data: {
    user_id?: string
    hashed_email?: string
    first_name?: string
    last_name?: string
    address: { city?: string; postal_code?: string; country?: string }
  }
  cookies: { fbc?: string; fbp?: string; gclid?: string; cje?: string }
}

// First-party cookies, so only readable in the browser — crm-service cannot see them.
const META_CLICK_COOKIE = '_fbc'
const META_BROWSER_COOKIE = '_fbp'
const GOOGLE_CLICK_COOKIE = '_gcl_aw'
const CJ_COOKIE = 'cje'
const CJ_LEGACY_COOKIE = 'cjevent'

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
  const value = match?.slice(name.length + 1)
  return value ? decodeURIComponent(value) : undefined
}

/** `_gcl_aw` is `GCL.<timestamp>.<gclid>`. The URL wins: the cookie may hold an older click. */
const readGclid = (): string | undefined => {
  if (typeof window !== 'undefined') {
    const fromUrl = new URLSearchParams(window.location.search).get('gclid')?.trim()
    if (fromUrl) return fromUrl
  }
  const cookie = readCookie(GOOGLE_CLICK_COOKIE)
  if (!cookie) return undefined
  const segments = cookie.split('.')
  return segments.length > 1 ? segments[segments.length - 1] : cookie
}

// Matches the product_type values the browser purchase events use.
const PRODUCT_TYPE_BY_PRIORITY: ReadonlyArray<{
  matches: (lookupKey: string, type: PriceProductType) => boolean
  name: string
}> = [
  { matches: (lookupKey) => lookupKey.includes('ZOE_PLUS'), name: 'membership' },
  { matches: (_, type) => type === PriceProductType.GUT_MICROBIOME_TEST, name: 'tests' },
  { matches: (lookupKey) => lookupKey.includes('DAILY30'), name: 'daily30' },
  { matches: (lookupKey) => lookupKey.includes('BARS'), name: 'gut-health-bar' },
]

/** Null when the session carries nothing worth reporting. */
export const buildSgtmPurchaseEvent = (
  session: GetCheckoutSessionDetailsResponse,
  countryCode?: string,
): SgtmPurchaseEvent | null => {
  const lineItems = excludePaymentApiShippingFeeLineItems(session.line_items ?? [])
  if (lineItems.length === 0) return null

  // Collapse trial pairs so the fee and its recurring charge report as one item, as the browser does.
  const combined = combineComplexTrialLineItems(lineItems)
  const country = countryCode || session.customer?.shipping_address?.country_code || undefined

  const productType = PRODUCT_TYPE_BY_PRIORITY.find(({ matches }) =>
    lineItems.some((item) => matches(item.price.lookup_key, item.price.product_type)),
  )?.name

  return {
    event_name: 'Purchase',
    event_time: Math.floor(Date.now() / 1000),
    event_id: session.session_id,
    page_data: { section: 'checkout', page_name: 'purchase' },
    purchase_data: {
      total_value: (session.amount_total || 0) / 100,
      currency: lineItems[0].price.currency.toUpperCase(),
      transaction_id: session.session_id,
      checkout_id: session.session_id,
      product_name: combined[0]?.price.lookup_key,
      product_type: productType,
      trial_length: combined.find((item) => item.trialLengthDays)?.trialLengthDays,
      items: combined.map((item) => {
        // amount_subtotal - amount_discount is the discounted line total; the container's `price` is
        // per unit, so divide by quantity or item revenue reads as price x quantity for quantity > 1.
        const quantity = item.quantity || 1
        return {
          item_id: item.price.lookup_key,
          item_name: item.price.nickname || undefined,
          price: ((item.amount_subtotal || 0) - (item.amount_discount || 0)) / 100 / quantity,
          quantity,
          item_brand: 'ZOE',
          item_category: productType,
          item_category4: country,
          trial_length_days: item.trialLengthDays,
        }
      }),
    },
    user_data: {
      user_id: hashPii(session.customer?.id),
      hashed_email: hashPii(session.customer?.email),
      first_name: hashPiiCompact(session.customer?.first_name),
      last_name: hashPiiCompact(session.customer?.last_name),
      address: {
        city: hashPiiCompact(session.customer?.shipping_address?.city),
        postal_code: hashPiiCompact(session.customer?.shipping_address?.postal_code),
        country: hashPii(session.customer?.shipping_address?.country_code || country),
      },
    },
    cookies: {
      fbc: readCookie(META_CLICK_COOKIE),
      fbp: readCookie(META_BROWSER_COOKIE),
      gclid: readGclid(),
      cje: readCookie(CJ_COOKIE) ?? readCookie(CJ_LEGACY_COOKIE),
    },
  }
}
